<template>
    <div class="tds-date-picker">
        <div class="month-switch mb-2">
            <span class="prev-year"
                  @click="add(-1, 'YEAR')"></span>
            <span class="prev-month"
                  @click="add(-1, 'MONTH')"></span>
            <span class="display">{{ monthYear }}</span>
            <span class="next-month"
                  @click="add(1, 'MONTH')"></span>
            <span class="next-year"
                  @click="add(1, 'YEAR')"></span>
        </div>
        <div class="week-days">

        </div>
        <div class="days-legend">
            <div class="day" v-for="day in daysLegend" :key="day.index">
                {{ day.shortName }}
            </div>
        </div>
        <div class="days-in-month">
            <div class="week" v-for="(week, index) in daysPerWeek"
                 :key="'week-' + index">
                <div class="day"
                     :class="{ highlighted: dayIsHighlighted(day), disabled: day.isOutOfMonth }"
                     v-for="day in week"
                     :key="day.id"
                     @click="select(day)">
                    {{ day.day }}
                </div>
            </div>
        </div>
        <hr>
        <div class="result-headline mb-4">
            <span class="float-right">
                <img src="../../assets/icons/globe-grey56.svg" alt="Timezone">
                UTC +0
            </span>
            <span>{{ $t("components.datePicker.displayStats") }}</span>
        </div>
        <div class="result-wrapper mb-4">
            <div class="date-result">
                <label for="start-time">{{ $t("general.from") }}</label>
                <span>
                    {{ selectedStartDay ? selectedStartDay.date.format('MMM DD, YYYY') : 'N/A' }}
                </span>
            </div>
            <div class="pl-4">
                <input id="start-time"
                       type="text"
                       :class="{ 'has-error': !startTimeValid }"
                       v-model="startTime"
                       @input="onStartTimeChange"
                       maxlength="5"
                       size="5"
                       placeholder="HH:mm">
            </div>
        </div>
        <div class="result-wrapper mb-4">
            <div class="date-result">
                <label for="end-time">{{ $t("general.to") }}</label>
                <span>
                    {{ selectedEndDay ? selectedEndDay.date.format('MMM DD, YYYY') : 'N/A' }}
                </span>
            </div>
            <div class="pl-4">
                <input id="end-time"
                       type="text"
                       :class="{ 'has-error': !endTimeValid }"
                       v-model="endTime"
                       @input="onEndTimeChange"
                       maxlength="5"
                       size="5"
                       placeholder="HH:mm">
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import {Component, Vue, Watch} from "vue-property-decorator";
import TDSDate, {TDSDateUnit} from "@/util/TDSDate";

interface WeekDayLegend {
    shortName: string;
    index: number;
}

interface Day {
    date: TDSDate;
    day: number;
    id: string;
    isOutOfMonth: boolean;
}

export interface TimeRange {
    start: TDSDate;
    end: TDSDate;
}

@Component
export default class TDSDatePicker extends Vue {
    private time: TDSDate = new TDSDate();
    private monthYear: string = this.time.format("MMM YYYY");
    private daysPerWeek: Array<Day[]> = [];
    private selectedStartDay: Day | null = null;
    private selectedEndDay: Day | null = null;
    private startTimeValid: boolean = true;
    private endTimeValid: boolean = true;
    private endTime: string = "00:00";
    private startTime: string = "00:00";

    // // // // // // // // // // HOOKS // // // // // // // // // //

    public created() {
        this.daysPerWeek = this.calculateDaysPerWeek();
    }

    // // // // // // // // // // WATCH // // // // // // // // // //

    @Watch("time")
    public onTimeChange() {
        this.monthYear = this.time.format("MMM YYYY");
        this.daysPerWeek = this.calculateDaysPerWeek();
    }

    @Watch("selectedStartDay")
    public selectedStartDayChange() {
        this.$emit("select", {
            start: this.selectedStartDay?.date,
            end: this.selectedEndDay?.date
        });
    }

    @Watch("selectedEndDay")
    public selectedEndDayChange() {
        this.$emit("select", {
            start: this.selectedStartDay?.date,
            end: this.selectedEndDay?.date
        });
    }

    // // // // // // // // // // GETTER // // // // // // // // // //

    private get daysLegend(): WeekDayLegend[] {
        return [...Array(7)].map((_: null, index: number): WeekDayLegend => {
            return {
                index,
                shortName: this.$t("general.weekDayShort." + index) + ""
            };
        });
    }

    // // // // // // // // // // METHODS // // // // // // // // // //

    private onStartTimeChange(event: InputEvent) {
        this.startTimeValid = true;
        const startTime: string = (event.target as HTMLInputElement).value;
        if (/[0-9]{2}:[0-9]{2}/.test(startTime)) { // HH:mm
            const date = this.selectedStartDay?.date;
            date?.setHours(Number(startTime.split(":")[0]));
            date?.setMinutes(Number(startTime.split(":")[1]));
            this.$emit("select", {
                start: this.selectedStartDay?.date,
                end: this.selectedEndDay?.date
            });
        } else {
            this.startTimeValid = false;
        }
    }

    private onEndTimeChange(event: InputEvent) {
        this.endTimeValid = true;
        const endTime: string = (event.target as HTMLInputElement).value;
        if (/[0-9]{2}:[0-9]{2}/.test(endTime)) { // HH:mm
            const date = this.selectedEndDay?.date;
            date?.setHours(Number(endTime.split(":")[0]));
            date?.setMinutes(Number(endTime.split(":")[1]));
            this.$emit("select", {
                start: this.selectedStartDay?.date,
                end: this.selectedEndDay?.date
            });
        } else {
            this.endTimeValid = false;
        }
    }

    private dayIsHighlighted(day: Day) {
        if (day.id === this.selectedStartDay?.id || day.id === this.selectedEndDay?.id) {
            return true;
        }
        return !!(this.selectedEndDay && this.selectedStartDay && day.date.getTime() < this.selectedEndDay.date.getTime() && day.date.getTime() > this.selectedStartDay.date.getTime());
    }

    private select(day: Day) {
        if (!this.selectedStartDay) {
            this.selectedStartDay = day;
            return;
        }
        if (!this.selectedEndDay) {
            if (day.date.getTime() > this.selectedStartDay.date.getTime())
                this.selectedEndDay = day;
            if (day.date.getTime() <= this.selectedStartDay.date.getTime()) {
                this.selectedEndDay = this.selectedStartDay;
                this.selectedStartDay = day;
            }
            return;
        }
        this.selectedStartDay = day;
        this.selectedEndDay = null;
    }

    private calculateDaysPerWeek(): Array<Day[]> {
        let loopDate = new TDSDate(this.time.getTime());
        loopDate.setHours(0);
        loopDate.setMinutes(0);
        loopDate.setDate(1);
        const dayOffset = loopDate.getDay();
        loopDate = loopDate.add(-dayOffset, TDSDateUnit.DAY);
        const days: Array<Day[]> = [];
        while (true) {
            let weekIndex = Math.floor((loopDate.getDate() + dayOffset - 1) / 7);
            let isOutOfMonth = false;
            if ((loopDate.getMonth() < this.time.getMonth() && loopDate.getFullYear() === this.time.getFullYear()) || loopDate.getFullYear() < this.time.getFullYear()) {
                weekIndex = 0;
                isOutOfMonth = true;
            } else if ((loopDate.getMonth() > this.time.getMonth() && loopDate.getFullYear() === this.time.getFullYear()) || loopDate.getFullYear() > this.time.getFullYear()) {
                if (days[days.length - 1].length === 7) return days;
                weekIndex = days.length - 1;
                isOutOfMonth = true;
            }
            if (!days[weekIndex]) days[weekIndex] = [];
            days[weekIndex].push({
                isOutOfMonth,
                date: loopDate,
                day: loopDate.getDate(),
                id: loopDate.format("YYYY-MM-DD")
            });
            loopDate = loopDate.add(1, TDSDateUnit.DAY);
        }
    }

    private add(amount: number, type: string) {
        switch (type) {
            case "YEAR":
                this.time = this.time.add(amount, TDSDateUnit.YEAR);
                break;
            case "MONTH":
                this.time = this.time.add(amount, TDSDateUnit.MONTH);
                break;
        }
    }
}
</script>

<style lang="scss"> // unscoped because of datepicker, is set global

$dayBoxWidth: 40px;

body {
    div.mx-datepicker-main.mx-datepicker-popup {
        z-index: 9998;
    }

    .tds-date-picker {
        width: $dayBoxWidth * 7;

        .month-switch {
            display: flex;
            flex-direction: row;

            span {
                display: block;
                width: 24px;
                height: 24px;
                flex: 1;
                background-repeat: no-repeat;
                background-position: 50% 50%;
                transition: transform 0.2s ease-out;

                &:nth-child(3) {
                    flex: 0 0 120px;
                    text-align: center;
                }

                &:hover {
                    cursor: pointer;
                    transform: scale(1.15);
                }

                &.prev-year {
                    background-image: url("../../assets/icons/nav_page_first.png");
                }

                &.prev-month {
                    background-image: url("../../assets/icons/nav_page_prev.png");
                }

                &.next-month {
                    background-image: url("../../assets/icons/nav_page_next.png");
                }

                &.next-year {
                    background-image: url("../../assets/icons/nav_page_last.png");
                }
            }
        }

        // end month swithc

        .days-legend {
            display: flex;
            flex-direction: row;

            .day {
                flex: 0 0 $dayBoxWidth;
                text-align: center;
            }
        }

        .days-in-month {
            min-height: $dayBoxWidth * 6;

            .week {
                display: flex;
                flex-direction: row;

                .day {
                    width: $dayBoxWidth;
                    height: $dayBoxWidth;
                    line-height: $dayBoxWidth;
                    text-align: center;
                    user-select: none;
                    transition: transform 0.2s ease-out;
                    color: $greyShade5;

                    &:hover {
                        cursor: pointer;
                        transform: scale(1.15);
                    }

                    &.highlighted {
                        background: $highlightColorShade2;
                        color: $highlightColor;
                        transform: scale(1) !important;
                    }

                    &.disabled {
                        pointer-events: none;
                        opacity: 0.3;
                    }
                }
            }
        }

        // end .days-in-month

        hr {
            background: none;
            border: solid 1px $greyShade2;
            margin: 4px 0 16px 0;
        }

        .result-headline {
            @include fontCaption;

            span {
                display: block;

                img {
                    display: inline-block;
                    width: 12px;
                    height: 12px;
                    margin-bottom: -2px; // o.O ... strange image baseline behaviour...
                }
            }
        }

        .result-wrapper {
            display: flex;
            flex-direction: row;

            > div {
                display: flex;
                flex-direction: column;

                &.date-result {
                    min-width: 90px;
                }

                label {
                    @include fontCaption;
                }

                input {
                    display: block;
                    width: 60px;
                    margin-top: 8px;
                    border-radius: 3px;
                    border: solid 1px $greyShade2;
                    font-size: 0.9rem;
                    font-family: "FFMark W04", "Helvetica Neue", Arial, sans-serif;
                    font-weight: 500;
                    line-height: 16px;
                    letter-spacing: -0.3px;
                    padding: 5px 8px;

                    &.has-error {
                        border: solid $tendencyRed 2px;
                    }
                }
            }
        }
    }

}

</style>
